import * as React from 'react'
import { createPortal } from 'react-dom'
import { ChartScanDetail } from '@/lib/api/company/chartScan'
import TachoDisc from '@/modules/drivers/tabs/activities/tacho_disc/TachoDisc'
import { fmt, useUnderscaleFactor, useChartT } from './i18n'

type Props = {
  companyId: string | number
  scans: ChartScanDetail[]
  onSaved?: () => void
  onFinish: () => void
}

// The editor paginates the discs of one scan on its own, so with several loaded
// files we walk the scans one by one: closing the editor moves to the next file
// and only the last one finishes the whole import.
export function StepReviewDiscs({ companyId, scans, onSaved, onFinish }: Props) {
  const T = useChartT()
  const withDiscs = React.useMemo(
    () => scans.filter((s) => (s.discs?.length ?? 0) > 0),
    [scans],
  )
  const [index, setIndex] = React.useState(0)
  const underscaleFactor = useUnderscaleFactor(scans)

  const current = withDiscs[index]
  const isLast = index >= withDiscs.length - 1


  const totalDiscCount = React.useMemo(
    () => withDiscs.reduce((sum, s) => sum + (s.discs?.length ?? 0), 0),
    [withDiscs],
  )
  const discsBefore = React.useMemo(
    () =>
      withDiscs
        .slice(0, index)
        .reduce((sum, s) => sum + (s.discs?.length ?? 0), 0),
    [withDiscs, index],
  )

  const handleClose = () => {
    if (isLast) onFinish()
    else setIndex((i) => i + 1)
  }

  if (!current) {
    return createPortal(
      <div className="tw-absolute tw-inset-0 tw-z-[209] tw-flex tw-items-center tw-justify-center tw-bg-black/40">
        <div className="tw-rounded-lg tw-bg-white tw-p-6 tw-text-center">
          <p className="tw-text-slate-700">
            {scans.length > 1
              ? T('noDiscsDetectedMulti', 'Nie wykryto tarczek na wczytanych skanach.')
              : T('noDiscsDetected', 'Nie wykryto tarczek na skanie.')}
          </p>
          {underscaleFactor && (
            <p className="tw-mt-2 tw-max-w-md tw-text-sm tw-text-slate-600">
              {fmt(
                T(
                  'lowResolutionScan',
                  'Skan ma zbyt niską rozdzielczość. Tarczki są na nim około {0}× za małe.'
                    + ' Zeskanuj w 300 dpi (tarczka powinna mieć około 1450 px średnicy).',
                ),
                underscaleFactor,
              )}
            </p>
          )}
          <button
            className="tw-mt-4 tw-rounded tw-bg-slate-200 tw-px-4 tw-py-2"
            onClick={onFinish}
          >
            {T('close', 'Zamknij')}
          </button>
        </div>
      </div>,
      document.body,
    )
  }

  return createPortal(
    <TachoDisc
      key={current.scanId}
      companyId={String(companyId)}
      discId={current.discs[0].discId}
      // A scan can hold several discs; load them all so the editor pages through
      // every one before we move on to the next file.
      includeSiblings
      positionOffset={discsBefore}
      totalDiscCount={totalDiscCount}
      hasMoreScans={!isLast}
      onClose={handleClose}
      onSaved={onSaved}
    />,
    document.body,
  )
}
